import * as React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00ff88] disabled:pointer-events-none disabled:opacity-50 font-mono uppercase tracking-wider',
  {
    variants: {
      variant: {
        default: 'bg-[#00ff8820] border border-[#00ff88] text-[#00ff88] hover:bg-[#00ff88] hover:text-black',
        destructive: 'bg-[#ff444420] border border-[#ff4444] text-[#ff4444] hover:bg-[#ff4444] hover:text-black',
        outline: 'border border-[#333] bg-transparent text-[#888] hover:bg-[#161616] hover:text-white hover:border-[#555]',
        ghost: 'bg-transparent border border-transparent text-[#888] hover:bg-[#161616] hover:text-white hover:border-[#333]',
        secondary: 'bg-[#1a1a1a] border border-[#222] text-[#888] hover:bg-[#222] hover:text-white',
      },
      size: {
        default: 'h-8 px-4 py-1.5 text-[10px]',
        sm: 'h-7 px-3 text-[9px]',
        lg: 'h-10 px-6 text-xs',
        icon: 'h-8 w-8',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  }
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return <Comp className={cn(buttonVariants({ variant, size, className }))} ref={ref} {...props} />
  }
)
Button.displayName = 'Button'
// eslint-disable-next-line react-refresh/only-export-components
export { Button, buttonVariants }
